/* eslint-disable react-hooks/exhaustive-deps */
import { isAlertWithin48Hours } from '@/Components/Common/deviceUtils';
import { HSCard, HSCardSkeleton, HSSearchInput, HSText } from '@/Components/Common/HSWidget';
import { HSModal } from '@/Components/Modals/HSModal';
import {
  CONFIG_DISPLAY_DEMOGRAPHIC_INFO_KEY,
  DEPLOYMENT_STATES,
  DEVICE_ALERTS_EVENTS,
  SESSION_STORAGE_KEYS,
} from '@/constants/device';
import { getLocaleTime } from '@/utilities/helpers/commonHelper';
import {
  AppstoreOutlined,
  DownOutlined,
  UnorderedListOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getUserConfig, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import useStartVideoCall from '@/Components/Hooks/useStartVideoCall';
import {
  isActionAllowed,
  isStaffActionAllowed,
  KnownActionsList,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import {
  formatFullNameDisplay,
  getUsername,
  isCare,
  isManage,
  isSocial,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  Actions,
  PhotoResolution,
  UserPermissionLevel,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { Col, Divider, Empty, Row, Skeleton, Spin, Tooltip } from 'antd';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMainContext } from '../../MainContext';
import MainPage from '../../MainPage';
import CustomButton, { ButtonTypes } from '../Common/CustomButton';
import FilterBar, { sanitizeFilters } from '@/Components/Common/FilterBar';
import {
  AddPlusIcon,
  AssignedBadgeIcon,
  AutoAnswerIcon,
  BadgeAddIcon,
  CalendarIcon,
  CloseIcon,
  ContactImage,
  ContactNewIcon,
  HC200Icon,
  MotionActivityIcon,
  NewHC200Icon,
  NewSocialIcon,
  NormalCallIcon,
  SmartHomeIcon,
  SocialIcon,
  UnassignedHS200DeviceIcon,
  UserHubsDoBIcon,
  UserHubsGenderIcon,
  UserHubsIncompleteContactImage,
} from '../Common/Icons';
import { WellnessIcon } from '../Common/Icons/WellnessIcon';
import RebootResetModal from '../Devices/RebootResetModal';
import useConfig from '../Hooks/useConfig';
import AutoCallModal from '../Modals/AutoCallModal';
import { getModalDataForCall } from '../Modals/AutoCallModalDataUtils';
import SendMessage from '../SendMessage/SendMessage';
import AssignStaffModal from '../Staff/NewAssignStaff/AssignStaffModal';
import { useAssignStaffHandler } from '../Staff/NewAssignStaff/useAssignStaffHandler';
import AddAccountFromUserHubs from './AddAccountFromUserHubs/AddAccountFromUserHubs';
import UserHubModal from './UserHubModal';
import {
  ActionButtonsDiv,
  AddAccountButton,
  CustomOfflineIcon,
  OnlineIcon,
  ShowMoreButton,
  TileAlertIcon,
  TileAlertRow,
  TileCard,
  TileInfo,
  TileInfoIcon,
  TileInfoRow,
  TileInfoValue,
  TileMainContent,
  TileName,
  TileProfileImage,
  TileProfileIncompleteText,
  TileProfileWrapper,
  TileRelation,
  TileViewCol,
  TileViewRow,
  UserHubsViewTabs,
} from './UserHubs.styles';

const showDevice = (hub) => !!hub.deviceuuid;
const hasAlerts = (hub) => Array.isArray(hub?.alerts) && hub.alerts.length > 0;

const ACCOUNT_FILTER_GROUP = 'accounts';
const ACCOUNT_FILTER_LABELS = {
  deployedWithClient: 'CLIENT',
  demoAccount: 'DEMO',
  myAccount: 'MYACCOUNTS',
  assignedToStaff: 'ASSIGNEDTOSTAFF',
  notAssignedToStaff: 'NOTASSIGNEDTOSTAFF',
  smartCameraOffline: 'SMARTCAMERAOFFLINE',
  smartCameraOnline: 'SMARTCAMERAONLINE',
};
const STAFF_ADMIN_ONLY_FILTERS = ['myAccount', 'assignedToStaff', 'notAssignedToStaff'];
const ACCOUNT_FILTER_MATCHERS = {
  deployedWithClient: (hub) => hub.deployment_state === 'DEPLOYED',
  demoAccount: (hub) =>
    !hub.deployment_state || hub.deployment_state === DEPLOYMENT_STATES.NOT_DEPLOYED,
  myAccount: (hub) => !!hub.myaccount,
  assignedToStaff: (hub) => !!hub.isstaffassigned,
  notAssignedToStaff: (hub) => !hub.isstaffassigned,
  smartCameraOffline: (hub) => hub.deviceconnectstatus === 'DISCONNECTED',
  smartCameraOnline: (hub) => hub.deviceconnectstatus === 'CONNECTED',
};

const UserHubsComponent = ({
  userHubs = [],
  fetchingUserHubs,
  isBackgroundFetching,
  onActionClick,
}) => {
  const { startCall } = useStartVideoCall();
  const trans = useSelector(translator);
  const { desktopNotification } = useMainContext();
  const currentUser = useSelector(getCurrentUser);
  const permissions = useSelector(getPermissions());
  const { useruuid } = useMemo(() => currentUser, [currentUser]);
  const currentUserInfo = useSelector(getUserInfo(useruuid));
  useConfig({ useruuid });

  const isStaff = currentUser.permission_level === UserPermissionLevel.staff;
  const isStaffAdmin = currentUser.permission_level === UserPermissionLevel.staffAdmin;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hubMessageData, setHubMessageData] = useState('');
  const [selectedHub, setSelectedHub] = useState('');
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [selectedViewTab, setSelectedViewTab] = useState(
    SessionStorage.get(SESSION_STORAGE_KEYS.ACCOUNTS_VIEW) || 'list'
  );
  const [hsModalDetails, setHSModalDetails] = useState({
    open: false,
    title: '',
    content: '',
    footer: '',
    type: '',
  });
  const [inputValue, setInputValue] = useState('');
  const [isMessageSendDisabled, setIsMessageSendDisabled] = useState(true);
  const messagingApi = useSelector((state) => state.apis.messagingApi);
  const currentUserConfig = useSelector(getUserConfig(useruuid));
  const messageRef = useRef(null);
  const [showMoreAttentionAccounts, setShowMoreAttentionAccounts] = useState(false);
  const [showMorePartiallyCompletedAccounts, setShowMorePartiallyCompletedAccounts] =
    useState(false);

  const shouldDisplayDemographicSection = (hub) => {
    const configValue = get(currentUserConfig, CONFIG_DISPLAY_DEMOGRAPHIC_INFO_KEY, true);
    if (!configValue) return false;
    if (isStaffAdmin) return true;
    return isStaff && (isManage(hub) || isCare(hub));
  };

  const alertsMap = useMemo(
    () => ({
      [DEVICE_ALERTS_EVENTS.INSIGHTS_SMART_HOME_ALERT_EVENT]: {
        classNames: 'smart-home-alert',
        label: trans('SMART_HOME'),
        Icon: SmartHomeIcon,
      },
      [DEVICE_ALERTS_EVENTS.INSIGHTS_BIOMETRICS_ALERT_EVENT]: {
        classNames: 'biometric-alert',
        label: trans('BIOMETRICS'),
        Icon: WellnessIcon,
      },
      [DEVICE_ALERTS_EVENTS.INSIGHTS_MOTION_ALERT_EVENT]: {
        classNames: 'motion-alert',
        label: trans('MOTION'),
        Icon: MotionActivityIcon,
      },
    }),
    [trans]
  );

  const openSubMenu = true;
  const {
    isFirstModalOpen,
    openAssignStaffModal,
    handleCloseFirstModal,
    selectedStaff,
    mOrderedStaffList,
    fetchingStaffList,
    handleAddStaff,
    formData,
    handleAssignStaff,
    handleRoleSelection,
    handleInputChange,
    handleOpenSecondModal,
    handleCloseSecondModal,
    isSecondModalOpen,
    setSelectedStaff,
    role,
  } = useAssignStaffHandler(selectedHub, onActionClick);

  const filterGroups = useMemo(
    () => [
      {
        key: ACCOUNT_FILTER_GROUP,
        multiple: true,
        children: Object.keys(ACCOUNT_FILTER_LABELS)
          .filter((key) => isStaffAdmin || !STAFF_ADMIN_ONLY_FILTERS.includes(key))
          .map((key) => ({ key, label: trans(ACCOUNT_FILTER_LABELS[key]) })),
      },
    ],
    [isStaffAdmin, trans]
  );

  const checkSearch = (hub, search) => {
    if (!search) return true;
    const hubname = getUsername(hub)?.toLowerCase();
    return hubname?.includes(search.toLowerCase());
  };

  // Selected options are OR-ed: an account is shown if it matches any of them.
  const checkFilter = (hub, filters) =>
    isEmpty(filters) || filters.some(({ itemKey }) => ACCOUNT_FILTER_MATCHERS[itemKey]?.(hub));

  /** search and filter variables */
  const [searchText, setSearchText] = useState('');
  const [savedFilters, setSavedFilters] = useState(
    () => SessionStorage.get(SESSION_STORAGE_KEYS.FILTER) || []
  );
  const appliedFilter = useMemo(
    () => sanitizeFilters(savedFilters, filterGroups),
    [savedFilters, filterGroups]
  );

  const hubList = useMemo(
    () =>
      userHubs
        .filter((hub) => checkSearch(hub, searchText))
        .filter((hub) => checkFilter(hub, appliedFilter)),
    [userHubs, searchText, appliedFilter]
  );

  const handleFilterChange = (filters) => {
    setSavedFilters(filters);
    SessionStorage.set(SESSION_STORAGE_KEYS.FILTER, filters);
  };

  const image = useMemo(
    () => selectedHub?.profilePics && selectedHub?.profilePics[PhotoResolution.R128],
    [selectedHub]
  );

  const videoCallNotificationModalVisible = useSelector(
    (state) => state.notification.videoCallNotificationModalVisible
  );

  useEffect(() => desktopNotification.requestPermission(), []);

  const autoAcceptCallHubs = useMemo(() => {
    if (userHubs.length > 0) {
      const autoAcceptMap = new Map();
      userHubs.forEach((hub) => {
        if (hub.useruuid && hub.autoacceptcall) {
          autoAcceptMap.set(hub.useruuid, hub.autoacceptcall);
        }
      });
      return autoAcceptMap;
    } else return new Map();
  }, [userHubs]);

  useEffect(() => {
    if (videoCallNotificationModalVisible) {
      setIsCallModalOpen(false);
      setIsModalOpen(false);
    }
  }, [videoCallNotificationModalVisible]);

  const allowCall = (hub) =>
    currentUser.useruuid !== hub.useruuid &&
    isActionAllowed(hub.rolename, KnownActionsList.call_contacts, permissions);

  const handleModal = (hub) => {
    setIsModalOpen(true);
    setSelectedHub(hub);
  };

  const handleSendMessage = () => {
    setShowModal(true);
    setHubMessageData(modalDataForSendMessage);
  };

  const handleCallModal = (hub) => {
    setSelectedHub(hub);
    setIsCallModalOpen(true);
  };

  const modalData = {
    image: image,
    devicename: getUsername(selectedHub),
    status: trans('OFFLINE'),
    cancel: () => setIsModalOpen(false),
  };

  const modalDataForSendMessage = {
    title: <div className="messagetitle">{trans('DEVICEOFFLINE')}</div>,
    description: <div className="messagedescription">{trans('MESSAGEISNOTSENT')}</div>,
    action: '',
    icon: <HC200Icon status={'disconnected'} />,
    dismiss: () => setShowModal(false),
  };

  const modalDataForCall = useMemo(
    () => getModalDataForCall(selectedHub, () => setIsCallModalOpen(false), startCall, trans),
    [selectedHub, startCall, trans]
  );

  const handleMessageButtonClick = (record) => {
    setSelectedHub(record);
    setHSModalDetails({
      ...hsModalDetails,
      open: true,
      type: 'message',
      title: <HSText className="title-blue">{trans('SENDMESSAGE')}</HSText>,
      content: (
        <SendMessage
          record={record}
          ref={messageRef}
          customClassName={'hide-buttons'}
          inputValue={inputValue}
          handleValueChange={setInputValue}
          handleSendDisabled={setIsMessageSendDisabled}
          onActionClick={onActionClick}
          onUserHubActionClick={onActionClick}
        />
      ),
    });
  };

  const postMessage = () => {
    const errorCallback = (error) => {
      console.error('Error while sending message:', error);
    };
    const successCallback = (data) => {
      setInputValue('');
      setIsMessageSendDisabled(true);
    };
    try {
      messagingApi.createUserNotification(
        useruuid,
        { message: inputValue, contacts: [{ useruuid: selectedHub?.useruuid }] },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleHSModalOk = () => {
    postMessage();
    setHSModalDetails({ ...hsModalDetails, open: false });
    messageRef.current?.removeMessage();
  };

  const handleHSModalCancel = () => {
    setIsMessageSendDisabled(true);
    messageRef.current?.removeMessage();
    setHSModalDetails({ ...hsModalDetails, open: false });
  };

  /** render functions */
  const renderHeaderActions = (
    <div className="header-action-container">
      <div className="search-container">
        <HSSearchInput
          autoFocus
          value={searchText}
          onChange={setSearchText}
          onClear={() => setSearchText('')}
        />
      </div>
      <FilterBar groups={filterGroups} selected={appliedFilter} onChange={handleFilterChange} />
    </div>
  );

  const renderUserType = (hub) => {
    const isManageRole = isManage(hub);
    const isCareRole = isCare(hub);
    const isSocialRole = isSocial(hub);

    if (isManageRole || isCareRole || isSocialRole) {
      return (
        <div className="type-text-block">
          <span className="type-text">
            {trans(isManageRole ? 'MANAGE' : isCareRole ? 'CARE' : 'SOCIAL')}
          </span>
        </div>
      );
    } else return null;
  };

  const renderUserHubListItem = (hub) => {
    const displayDemographicInfo = shouldDisplayDemographicSection(hub);
    const contactsTooltipText =
      hub?.numofcontacts <= 0 || hub?.numofcontacts === null
        ? trans('NOCONTACTS')
        : hub?.numofadmincontacts <= 0 || hub?.numofadmincontacts === null
          ? trans('NOACCOUNTMANAGER')
          : '';
    const applyRedHover = [trans('NOCONTACTS'), trans('NOACCOUNTMANAGER')].includes(
      contactsTooltipText
    );
    const userTypeContent = renderUserType(hub); // Capture renderUserType content
    const isDemoAccount = hub?.deployment_state === DEPLOYMENT_STATES.NOT_DEPLOYED;
    const showAlerts = hasAlerts(hub);
    const image = hub.profilePic64;
    const isIncomplete = !hub?.isaccountsetupcompleted;

    return (
      <HSCard
        key={hub.hubid}
        className="item-container hub-container container-card-css"
        showAlert={showAlerts}
        onClick={onActionClick(Actions.hubDetails, hub, openSubMenu)}
        content={
          <Col className="account-card-container">
            <Row className="account-details-container">
              <div
                className="container-1"
                role="button"
                tabIndex={0}
                onClick={onActionClick(Actions.hubDetails, hub, openSubMenu)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onActionClick(Actions.hubDetails, hub, openSubMenu)();
                  }
                }}
              >
                <div className="user-info-container">
                  <div className="image-container">
                    <>
                      {image ? (
                        <>
                          <img
                            src={image}
                            className={`hub-header-user-profile-pic ${isIncomplete ? 'yellow-border' : 'image'}`}
                            alt=""
                          />
                          {isIncomplete && (
                            <div className="incomplete-container">
                              <span className="incomplete-text">{trans('INCOMPLETE')}</span>
                            </div>
                          )}
                        </>
                      ) : isIncomplete ? (
                        <UserHubsIncompleteContactImage text={trans('INCOMPLETE')} />
                      ) : (
                        <ContactImage className="hub-header-user-profile-pic svg" />
                      )}
                    </>
                  </div>
                  <div className="info-container">
                    <div className="username">
                      <Tooltip title={getUsername(hub)}>
                        <>{formatFullNameDisplay(hub?.firstname, hub?.lastname)}</>
                      </Tooltip>
                    </div>
                    <div className="user-hubs-client-info-container">
                      {displayDemographicInfo && (
                        <div className="user-hubs-dob-gender-group">
                          <div className="user-hubs-gender-block">
                            <UserHubsGenderIcon className="user-hubs-gender-icon" />
                            <span className="user-hubs-gender-label">
                              {hub.gender
                                ? trans(hub.gender.toUpperCase().replace(/\s/g, ''))
                                : trans('NOTKNOWN')}
                            </span>
                          </div>
                          <div className="user-hubs-dob-block">
                            <UserHubsDoBIcon className="user-hubs-dob-icon" />
                            <span className="user-hubs-dob-label">
                              {hub?.dateofbirth
                                ? getLocaleTime(hub?.dateofbirth, currentUserInfo?.locale)
                                : trans('NOTKNOWN')}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="relation">{hub.relationship}</div>
                    </div>
                  </div>
                </div>
                {isDemoAccount && (
                  <div className="user-hubs-demo-tag-container">
                    <span className="user-hubs-demo-tag-text">{trans('DEMO')}</span>
                  </div>
                )}
                {userTypeContent && <div className="usertype-container">{userTypeContent}</div>}
              </div>
              <div className="container-2" onClick={(e) => e.stopPropagation()}>
                <div className="actions-container">
                  {allowCall(hub) && autoAcceptCallHubs.size ? (
                    <Tooltip title={trans('VIDEOCALL')}>
                      <div
                        className="action"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          hub?.deviceconnectstatus === 'CONNECTED'
                            ? autoAcceptCallHubs.get(hub.useruuid)
                              ? handleCallModal(hub)
                              : startCall({ callees: [hub], hubRecord: hub })
                            : handleModal(hub);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            hub?.deviceconnectstatus === 'CONNECTED'
                              ? autoAcceptCallHubs.get(hub.useruuid)
                                ? handleCallModal(hub)
                                : startCall({ callees: [hub], hubRecord: hub })
                              : handleModal(hub);
                          }
                        }}
                      >
                        {autoAcceptCallHubs.get(hub.useruuid) ? (
                          <AutoAnswerIcon className="user-hubs-common-action-icon" />
                        ) : (
                          <NormalCallIcon className="user-hubs-common-action-icon" />
                        )}
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="empty-action" />
                  )}
                  {isActionAllowed(hub.rolename, KnownActionsList.call_contacts, permissions) ? (
                    <Tooltip title={trans('MESSAGE')}>
                      <div
                        className="action"
                        role="button"
                        tabIndex={0}
                        onClick={
                          hub?.deviceconnectstatus?.toLowerCase() === 'connected'
                            ? () => handleMessageButtonClick(hub)
                            : handleSendMessage
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            hub?.deviceconnectstatus?.toLowerCase() === 'connected'
                              ? handleMessageButtonClick(hub)
                              : handleSendMessage();
                          }
                        }}
                      >
                        <NewSocialIcon className="user-hubs-common-action-icon" />
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="empty-action" />
                  )}
                </div>
                {isStaffActionAllowed(
                  currentUser?.permission_level,
                  KnownActionsList.manage_assign_staff,
                  permissions
                ) && (
                  <>
                    <Divider type="vertical" className="user-hubs-list-divider" />
                    <Tooltip title={trans('ASSIGNSTAFF')}>
                      <div className="staff-add-icon-container">
                        {hub?.isstaffassigned ? (
                          <AssignedBadgeIcon
                            className="staff-add-icon"
                            onClick={() => {
                              setSelectedHub(hub);
                              openAssignStaffModal();
                            }}
                          />
                        ) : (
                          <BadgeAddIcon
                            className="staff-add-icon staff-not-assigned-add-icon"
                            onClick={() => {
                              setSelectedHub(hub);
                              openAssignStaffModal();
                            }}
                          />
                        )}
                      </div>
                    </Tooltip>
                  </>
                )}
                <Divider type="vertical" className="user-hubs-list-divider" />
                <div className="actions-container">
                  {isActionAllowed(hub.rolename, KnownActionsList.create_events, permissions) ? (
                    <Tooltip title={trans('SCHEDULEEVENT')}>
                      <div
                        className="action"
                        role="button"
                        tabIndex={0}
                        onClick={onActionClick(Actions.addEvent, hub)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onActionClick(Actions.addEvent, hub)();
                          }
                        }}
                      >
                        <CalendarIcon className="user-hubs-common-action-icon" />
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="empty-action" />
                  )}
                </div>
                <Divider type="vertical" className="user-hubs-list-divider" />
                <Tooltip title={contactsTooltipText}>
                  <div
                    className="association-count-container"
                    role="button"
                    tabIndex={0}
                    onClick={onActionClick(Actions.listContacts, hub, true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onActionClick(Actions.listContacts, hub, true)();
                      }
                    }}
                  >
                    <ContactNewIcon
                      className={`count-icon-new ${applyRedHover ? 'red-icon' : ''}`}
                    />
                    <div className="count-container">
                      <span className={`count-text ${applyRedHover ? 'red-text' : ''}`}>
                        {hub.numofcontacts ?? 0}
                      </span>
                    </div>
                  </div>
                </Tooltip>
                <Divider type="vertical" className="user-hubs-list-divider" />
                <div className="device-icon-container">
                  {showDevice(hub) ? (
                    <NewHC200Icon
                      status={
                        hub?.deviceconnectstatus?.toLowerCase() === 'connected'
                          ? 'connected'
                          : 'disconnected'
                      }
                    />
                  ) : (
                    <UnassignedHS200DeviceIcon className="user-hubs-list-device-red-icon" />
                  )}
                </div>
              </div>
            </Row>
            {showAlerts && (
              <Row
                className="hub-alerts"
                role="button"
                tabIndex={0}
                onClick={onActionClick(Actions.hubDetails, hub, openSubMenu)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onActionClick(Actions.hubDetails, hub, openSubMenu)();
                  }
                }}
              >
                {hub.alerts.map((alert, index) => {
                  const alertData = alertsMap[alert.alert];
                  if (!alertData) return null;
                  const { classNames, label, Icon } = alertData;
                  return (
                    <div key={`${alert.alert}-${index}`} className={classNames}>
                      <Icon className="alert-icon" />
                      <span className="alert-label">{label}</span>
                    </div>
                  );
                })}
              </Row>
            )}
          </Col>
        }
      />
    );
  };

  const renderUserHubTileItem = (hub) => {
    const displayDemographicInfo = shouldDisplayDemographicSection(hub);
    const isConnected = hub?.deviceconnectstatus?.toLowerCase() === 'connected';
    const showAlerts = hasAlerts(hub);
    const image = hub.profilePic64;
    const isIncomplete = !hub?.isaccountsetupcompleted;

    return (
      <TileViewCol key={hub.hubid}>
        <TileCard
          showAlert={showAlerts}
          onClick={onActionClick(Actions.hubDetails, hub, openSubMenu)}
          content={
            <TileMainContent>
              <TileProfileWrapper>
                {image ? (
                  <>
                    <TileProfileImage src={image} $isIncomplete={isIncomplete} alt="" />
                    {isIncomplete && (
                      <TileProfileIncompleteText>
                        <span className="incomplete-text">{trans('INCOMPLETE')}</span>
                      </TileProfileIncompleteText>
                    )}
                  </>
                ) : isIncomplete ? (
                  <UserHubsIncompleteContactImage text={trans('INCOMPLETE')} />
                ) : (
                  <ContactImage className="hub-header-user-profile-pic svg" />
                )}
                {showDevice(hub) &&
                  (isConnected ? (
                    <OnlineIcon />
                  ) : (
                    <CustomOfflineIcon $isIncomplete={image && isIncomplete} />
                  ))}
              </TileProfileWrapper>

              <Tooltip title={getUsername(hub)}>
                <TileName>{formatFullNameDisplay(hub?.firstname, hub?.lastname)}</TileName>
              </Tooltip>

              <TileInfo>
                {displayDemographicInfo && (
                  <>
                    <TileInfoRow>
                      <TileInfoIcon>
                        <UserHubsGenderIcon />
                      </TileInfoIcon>
                      <TileInfoValue>
                        {hub.gender
                          ? trans(hub.gender.toUpperCase().replace(/\s/g, ''))
                          : trans('NOTKNOWN')}
                      </TileInfoValue>
                    </TileInfoRow>
                    <TileInfoRow>
                      <TileInfoIcon>
                        <UserHubsDoBIcon />
                      </TileInfoIcon>
                      <TileInfoValue>
                        {hub?.dateofbirth
                          ? getLocaleTime(hub?.dateofbirth, currentUserInfo?.locale)
                          : trans('NOTKNOWN')}
                      </TileInfoValue>
                    </TileInfoRow>
                  </>
                )}
                <TileRelation>{hub.relationship}</TileRelation>
              </TileInfo>
              {showAlerts && (
                <TileAlertRow>
                  {hub.alerts.map((alert, index) => {
                    const alertData = alertsMap[alert.alert];
                    if (!alertData) return null;
                    const { label, Icon } = alertData;
                    return (
                      <Tooltip key={`${alert.alert}-${index}`} title={label}>
                        <TileAlertIcon>
                          <Icon />
                        </TileAlertIcon>
                      </Tooltip>
                    );
                  })}
                </TileAlertRow>
              )}
            </TileMainContent>
          }
        />
      </TileViewCol>
    );
  };

  const renderUserHub = (accounts, showMore = true) => {
    if (selectedViewTab === 'list') {
      const displayedAccounts = showMore ? accounts : accounts.slice(0, 1);
      return displayedAccounts.map(renderUserHubListItem);
    } else if (selectedViewTab === 'tile') {
      return <TileViewRow>{accounts.map(renderUserHubTileItem)}</TileViewRow>;
    } else return null;
  };

  const normalizedHubList = useMemo(() => {
    return hubList.map((hub) => {
      const recentAlerts = (hub?.alerts || []).filter(isAlertWithin48Hours);
      const latestRecentAlertTimestamp = recentAlerts.reduce((latest, alert) => {
        const timestamp = Date.parse(alert?.last_receivedtime_utc || '');
        if (Number.isNaN(timestamp)) {
          return latest;
        }
        return Math.max(latest, timestamp);
      }, 0);

      return {
        ...hub,
        alerts: recentAlerts,
        latestRecentAlertTimestamp,
      };
    });
  }, [hubList]);

  const attentionAccounts = useMemo(
    () =>
      normalizedHubList
        .filter((hub) => hasAlerts(hub))
        .sort((a, b) => b.latestRecentAlertTimestamp - a.latestRecentAlertTimestamp),
    [normalizedHubList]
  );

  const [partiallyCompletedHubs, fullyCompletedHubs] = useMemo(() => {
    const partial = [];
    const full = [];

    normalizedHubList.forEach((hub) => {
      if (hasAlerts(hub)) return;

      if (hub?.isaccountsetupcompleted === false) {
        partial.push(hub);
      } else {
        full.push(hub);
      }
    });
    return [partial, full];
  }, [normalizedHubList]);

  const renderToggleViewButtons = () => (
    <UserHubsViewTabs
      activeKey={selectedViewTab}
      onChange={(key) => {
        setSelectedViewTab(key);
        SessionStorage.set(SESSION_STORAGE_KEYS.ACCOUNTS_VIEW, key);
      }}
      items={[
        {
          key: 'list',
          label: (
            <span className="user-hubs-view-tab-icon user-hubs-view-tab-list">
              <UnorderedListOutlined />
            </span>
          ),
        },
        {
          key: 'tile',
          label: (
            <span className="user-hubs-view-tab-icon user-hubs-view-tab-tile">
              <AppstoreOutlined />
            </span>
          ),
        },
      ]}
    />
  );

  const renderAddAccountButton = () => (
    <AddAccountButton
      role="button"
      tabIndex={0}
      onClick={() => setIsAddAccountModalOpen(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsAddAccountModalOpen(true);
        }
      }}
    >
      <div className="user-hubs-add-account-block">
        <AddPlusIcon className="user-hubs-add-account-icon" />
        <div className="user-hubs-add-account-label-block">
          <span className="user-hubs-add-account-label">{trans('ADD')}</span>
        </div>
      </div>
    </AddAccountButton>
  );

  const renderLoading = () => {
    if (selectedViewTab === 'tile') {
      return (
        <TileViewRow>
          {Array.from({ length: 5 }, (_, index) => (
            <TileViewCol key={index}>
              <TileCard
                content={
                  <TileMainContent>
                    <TileProfileWrapper>
                      <Skeleton.Avatar active size={60} shape="circle" />
                    </TileProfileWrapper>
                    <Skeleton.Input active size="large" style={{ width: 120 }} />
                    <TileInfo>
                      <Skeleton.Input active size="small" style={{ width: 90 }} />
                      <Skeleton.Input active size="small" style={{ width: 90 }} />
                    </TileInfo>
                  </TileMainContent>
                }
              />
            </TileViewCol>
          ))}
        </TileViewRow>
      );
    }
    return <HSCardSkeleton />;
  };

  const renderShowMoreButton = (count, showMore, setShowMore) => {
    if (selectedViewTab === 'tile') return null;
    if (count <= 1) return null;
    return (
      <ShowMoreButton
        role="button"
        tabIndex={0}
        onClick={() => setShowMore((value) => !value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowMore((value) => !value);
          }
        }}
      >
        <span className="show-more-label">{showMore ? trans('VIEWLESS') : trans('VIEWMORE')}</span>
        {showMore ? (
          <UpOutlined className="show-more-icon" />
        ) : (
          <DownOutlined className="show-more-icon" />
        )}
      </ShowMoreButton>
    );
  };

  return (
    <>
      {showModal && <RebootResetModal data={hubMessageData} />}
      {isModalOpen && <UserHubModal data={modalData} />}
      {isCallModalOpen && <AutoCallModal data={modalDataForCall} />}
      <AssignStaffModal
        isFirstModalOpen={isFirstModalOpen}
        handleCloseFirstModal={handleCloseFirstModal}
        fetchingStaffList={fetchingStaffList}
        selectedStaff={selectedStaff}
        mOrderedStaffList={mOrderedStaffList}
        handleAddStaff={handleAddStaff}
        formData={formData}
        handleInputChange={handleInputChange}
        selectedUser={getUsername(selectedHub)}
        handleAssignStaff={handleAssignStaff}
        handleRoleSelection={handleRoleSelection}
        handleOpenSecondModal={handleOpenSecondModal}
        handleCloseSecondModal={handleCloseSecondModal}
        isSecondModalOpen={isSecondModalOpen}
        setSelectedStaff={setSelectedStaff}
        role={role}
        hubRecord={selectedHub}
      />
      <MainPage hasSider={true}>
        <div className="userhubs-wrapper page-content-wrapper">
          <div className="page-header-container page-header-container-css">
            <div className="page-header">
              <span>{trans(isStaffAdmin ? 'ACCOUNTS' : 'MYACCOUNTS')}</span>
            </div>
            {renderHeaderActions}
          </div>
          <div className="item-count-container accounts-count-container-css">
            <div className="user-hubs-main-count-add-container">
              <span className="item-count">
                <div className="accounts-count">
                  <div className="accounts-count-header">
                    {(searchText || appliedFilter.length > 0) &&
                      `${hubList.length} ${trans('FOUND_L')} | `}
                    {trans('TOTAL')}
                    {' ('}
                    {userHubs.length ?? 0}
                    {')'}
                    {isBackgroundFetching && !fetchingUserHubs && (
                      <Spin className="user-hubs-background-fetching-spin" />
                    )}
                  </div>
                  <span className="accounts-count-text">
                    {trans('ACCOUNTSHAVEBEENSORTEDBYALPHABETICALORDER')}
                  </span>
                </div>
              </span>
            </div>
            <ActionButtonsDiv style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
              {renderToggleViewButtons()}
              {isStaffAdmin && renderAddAccountButton()}
            </ActionButtonsDiv>
            <AddAccountFromUserHubs
              isAddAccountModalOpen={isAddAccountModalOpen}
              setIsAddAccountModalOpen={setIsAddAccountModalOpen}
              onActionClick={onActionClick}
            />
          </div>
          <div className="item-list-container hub-list-container">
            {fetchingUserHubs ? (
              renderLoading()
            ) : !hubList.length ? (
              <Empty />
            ) : (
              <>
                {!isEmpty(attentionAccounts) && (
                  <div className="user-hubs-hub-list-partially-completed-main-container">
                    <div className="user-hubs-hub-list-partial-full-completed-container initial">
                      <span className="user-hubs-hub-list-partial-full-completed-label">
                        {`${trans('ACCOUNTS_NEEDING_ATTENTION')} (${attentionAccounts.length})`}
                      </span>
                      <span className="user-hubs-hub-list-partial-full-completed-info-text">
                        {trans('ACCOUNTSHAVEBEENSORTEDBYMOSTRECENTALERT')}
                      </span>
                    </div>
                    {renderUserHub(attentionAccounts, showMoreAttentionAccounts)}
                    {renderShowMoreButton(
                      attentionAccounts.length,
                      showMoreAttentionAccounts,
                      setShowMoreAttentionAccounts
                    )}
                  </div>
                )}
                {!isEmpty(partiallyCompletedHubs) && (
                  <div className="user-hubs-hub-list-partially-completed-main-container">
                    <div className="user-hubs-hub-list-partial-full-completed-container">
                      <span className="user-hubs-hub-list-partial-full-completed-label">
                        {`${trans('PARTIALLY_COMPLETEDACCOUNTS_L')} (${partiallyCompletedHubs.length})`}
                      </span>
                    </div>
                    {renderUserHub(partiallyCompletedHubs, showMorePartiallyCompletedAccounts)}
                    {renderShowMoreButton(
                      partiallyCompletedHubs.length,
                      showMorePartiallyCompletedAccounts,
                      setShowMorePartiallyCompletedAccounts
                    )}
                  </div>
                )}
                {!isEmpty(fullyCompletedHubs) && (
                  <div
                    className={`user-hubs-hub-list-partially-completed-main-container ${
                      !isEmpty(partiallyCompletedHubs)
                        ? 'user-hubs-hub-list-fully-completed-main-container'
                        : ''
                    }`}
                  >
                    <div className="user-hubs-hub-list-partial-full-completed-container">
                      <span className="user-hubs-hub-list-partial-full-completed-label">
                        {`${trans('FULLY_COMPLETEDACCOUNTS_L')} (${fullyCompletedHubs.length})`}
                      </span>
                    </div>
                    {renderUserHub(fullyCompletedHubs)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <HSModal
          open={hsModalDetails.open}
          handleOk={handleHSModalOk}
          title={hsModalDetails.title}
          handleCancel={handleHSModalCancel}
          content={hsModalDetails.content}
          className={`${hsModalDetails.type} user-hubs-modal`}
          footer={
            hsModalDetails.type === 'message' ? (
              <>
                <CustomButton
                  label={trans('CANCEL_U')}
                  icon={<CloseIcon className="cancel-icon" />}
                  onClick={handleHSModalCancel}
                />
                <CustomButton
                  type={ButtonTypes.primary}
                  label={trans('SENDNOW_U')}
                  icon={<SocialIcon />}
                  onClick={handleHSModalOk}
                  disabled={isMessageSendDisabled}
                />
              </>
            ) : null
          }
        />
      </MainPage>
    </>
  );
};

UserHubsComponent.propTypes = {
  userHubs: PropTypes.arrayOf(PropTypes.object),
  fetchingUserHubs: PropTypes.bool,
  isBackgroundFetching: PropTypes.bool,
  onActionClick: PropTypes.func,
  openSubMenu: PropTypes.bool,
};

export default UserHubsComponent;
